import dynamic from 'next/dynamic';
import { Suspense, useRef } from 'react';
import {
  ARTICLE_PAGE_SCHEMA,
  ORG_SCHEMA,
  RATING_SCHEMA,
  WEB_SCHEMA,
} from '../../constants/componentStaticData';
import { ITR_ARTICLE_SCHEMA_TEMPLATE } from '../../constants/itrContants';
import { useDataForPlansPricing } from '../S/pricing/pricing.hooks';
import {
  brandName,
  canonical,
  description,
  keywords,
  pageTitle,
} from './itr-contants';
import { FileItrSection } from './page-sections/file-itr-section';
import { HERO_SECTION_CONSTANTS, ITR_FAQs } from './static/data';
import { ITR_DATA } from './static/types';
import HeroSection from './page-sections/hero-section-new';
import { PricingCardWrapperV1 } from '../S/pricing/components/pricing-v1/pricing-card-carousel-v1.component';
import { PricingCardStaticDetailsV1_1 } from '../S/pricing/components/pricing-v1/card-details-constants-v1';
import SimpleStepsSection from './page-sections/simple-steps-section';
import WhyUseClearTaxSection from './page-sections/why-use-cleartax-section';
import { SectionHeader } from '../partner-pages/utils/section-header';
import StartFilingSection from './page-sections/start-filing-section';
import NoticeChanceSection from './page-sections/notice-chance-section';
import WhyWaitBanner from './page-sections/why-wait-banner';
import { TEASPlans } from '../S/pricing/types';
import { getEASPlanList } from '../S/pricing/components/eas_fold/utils';
import { ApiPlan } from './page-sections/hero-section/view-new-plan-modal-content/types';
import { StartForFreeSection } from './page-sections/start-for-free-section';

const FaqComponent = dynamic(
  () => import('../../components/Common/FAQS').then((mod) => mod.FaqComponent),
  { suspense: true }
);

interface ITR_PROPS {
  itrData: ITR_DATA;
  isVariant?: boolean;
  lastUpdatedDate?: string;
  pricingPlanData?: TEASPlans;
  diyPlans: ApiPlan[];
}

export const IncomeTaxPage = ({
  itrData,
  isVariant = false,
  pricingPlanData,
  diyPlans,
}: ITR_PROPS): JSX.Element => {
  const pricingPlanData_obj = useDataForPlansPricing(
    pricingPlanData ? getEASPlanList(pricingPlanData) : []
  );

  return (
    <>
      <HeroSection constants={HERO_SECTION_CONSTANTS} />
      <SimpleStepsSection />
      <WhyUseClearTaxSection />
      <div className="xl:block lg:block hidden">
        <StartFilingSection plans={diyPlans} />
      </div>
      <div className="md:block sm:block hidden">
        <StartForFreeSection
          id="start-for-free"
          name="Start For Free"
          priceMapData={pricingPlanData_obj}
          planId={'salary-income-plan'}
          plans={diyPlans}
          ref={useRef<HTMLDivElement>(null)}
        />
      </div>
      <div className="wg-container py-20">
        <SectionHeader
          heading="Need an expert to help with taxes?"
          description="Get a dedicated tax expert to handle everything, from start to finish"
          className="font-semibold text-s-46 sm:text-s-24 md:text-s-28 lg:text-s-32"
          descriptionClassName="sm:text-s-14 md:text-base lg:text-s-20 xl:text-s-24 text-center sm:px-10"
        />
        <PricingCardWrapperV1
          pricingPlanData={pricingPlanData_obj}
          PricingCardStaticDetails={PricingCardStaticDetailsV1_1}
          classname=""
        />
      </div>
      <div className="xl:block lg:block hidden">
        <NoticeChanceSection />
      </div>
      {/* <NewTestimonialSection /> */}
      <div className="xl:block lg:block hidden">
        <WhyWaitBanner />
      </div>
      <FileItrSection
        data={itrData.itrSection}
        id={`file-itr`}
        name={'File with ClearTax?'}
        ref={useRef<HTMLDivElement>(null)}
        key={`file-itr`}
        isVariant={isVariant}
      />
      <div className="wg-container">
        <Suspense fallback={<div>Loading FAQs...</div>}>
          <FaqComponent
            heading="Frequently Asked Questions"
            variant="partner"
            expanded="firstOnly"
            FAQS={ITR_FAQs}
            faqIconColor={'#314259'}
            isShowSchema={true}
          />
        </Suspense>
      </div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ORG_SCHEMA) }}
      ></script>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            WEB_SCHEMA({
              title: pageTitle,
              metaDesc: description,
              url: canonical,
            })
          ),
        }}
      ></script>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            RATING_SCHEMA({
              title: pageTitle,
              metaDesc: description,
              brandName: brandName,
              ratingValue: '4.9',
              reviewCount: '45682',
            })
          ),
        }}
      ></script>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            ARTICLE_PAGE_SCHEMA({
              title: pageTitle,
              metaDesc: description,
              productType: 'itr',
              keywords: keywords,
              datePublished: ITR_ARTICLE_SCHEMA_TEMPLATE.datePublished,
            })
          ),
        }}
      ></script>
    </>
  );
};
